import React from "react";
import { Link } from "react-router-dom";
import { motion as m } from "framer-motion";
import EditorialCover from "./EditorialCover";
import { Spotlight } from "./ui";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const ArticleCard = ({ article, index = 0, variant = "default" }) => {
  const { slug, title, dek, category, date, readTime, img, accent } = article;

  if (variant === "row") {
    return (
      <m.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{
          duration: 0.6,
          delay: Math.min(index, 6) * 0.05,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        <Link
          to={`/blog/${slug}`}
          className="group grid grid-cols-1 items-baseline gap-3 border-t border-[var(--color-border)] py-7 transition-colors duration-300 hover:border-azure dark:hover:border-electric md:grid-cols-12 md:gap-6"
        >
          <span className="t-eyebrow md:col-span-2">{formatDate(date)}</span>
          <h3 className="font-display text-2xl leading-snug transition-colors duration-300 group-hover:text-azure dark:group-hover:text-electric md:col-span-6 md:text-[1.6rem]">
            {title}
          </h3>
          <span className="t-eyebrow md:col-span-3">{category}</span>
          <span className="t-eyebrow md:col-span-1 md:text-right">
            {readTime} min
          </span>
        </Link>
      </m.div>
    );
  }

  return (
    <m.article
      layout
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        duration: 0.7,
        delay: Math.min(index, 5) * 0.06,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="group"
    >
      <Link to={`/blog/${slug}`} className="block">
        <Spotlight
          radius={400}
          intensity={0.16}
          className="relative aspect-[3/2] overflow-hidden rounded-lg bg-paper-warm shadow-card ring-1 ring-[var(--color-border)] transition-[box-shadow,transform] duration-500 ease-standard group-hover:-translate-y-1.5 group-hover:shadow-card-hover dark:bg-night-surface"
        >
          {img ? (
            <img
              src={img}
              alt={title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-standard group-hover:scale-[1.06]"
            />
          ) : (
            <div className="h-full w-full transition-transform duration-[900ms] ease-standard group-hover:scale-[1.04]">
              <EditorialCover title={title} kicker={category} accent={accent} />
            </div>
          )}
        </Spotlight>

        <div className="mt-5">
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted dark:text-slightwhite/55">
            <span className="text-azure dark:text-electric">{category}</span>
            <span className="h-px w-5 bg-[var(--color-border-strong)]" />
            <span className="tnum">{readTime} min read</span>
          </div>
          <h3 className="mt-3 font-display text-2xl leading-tight transition-colors duration-300 group-hover:text-azure dark:group-hover:text-electric md:text-[1.75rem]">
            {title}
          </h3>
          <p className="mt-3 text-[15px] leading-relaxed text-ink-muted dark:text-slightwhite/65">
            {dek}
          </p>
        </div>
      </Link>
    </m.article>
  );
};

export default ArticleCard;
